import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { todayStr } from '../lib/format.js';

// ─── Fixed costs (Settings → Fixed costs, owner-only) ─────────────────────
// CJ 2026-07-22. The standing & metered overhead — rent, utilities, insurance,
// property tax, licenses, professional fees, subscriptions — that turns the
// per-café prime-cost report into a full weekly/daily P&L. Attributable to a
// café, a legal entity (LLC), or the whole org; effective-month dated.
// Backend: /api/admin/fixed-costs (migration 097). Phase 1: capture the data.

const CATEGORIES = [
  { key: 'rent', label: 'Rent (base)' },
  { key: 'cam', label: 'CAM' },
  { key: 'nnn', label: 'NNN / triple-net' },
  { key: 'percentage_rent', label: 'Percentage rent' },
  { key: 'utilities', label: 'Utilities' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'subscription', label: 'Subscription' },
  { key: 'property_tax', label: 'Property tax' },
  { key: 'license', label: 'License' },
  { key: 'professional_fees', label: 'Professional fees' },
  { key: 'other', label: 'Other…' },
];
const ENTITIES = ['ETC', 'NBT'];
const PERIODS = [['monthly', '/ mo'], ['quarterly', '/ qtr'], ['annual', '/ yr']];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthOf = (d) => (d || '').slice(0, 7);
const catLabel = (k) => CATEGORIES.find((c) => c.key === k)?.label.replace('…', '') || String(k || '').replace(/_/g, ' ');
const periodShort = (p) => PERIODS.find((x) => x[0] === p)?.[1] || '/ mo';

export function FixedCostsView({ currentUser, locations }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | <id>
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState('');

  // ET, not UTC. Fixed costs are effective-month dated, and on the evening of
  // the last day of a month a UTC slice rolls to the next one — so the form
  // defaulted a rent change into the wrong month. todayStr() is ET.
  const thisMonth = todayStr().slice(0, 7);
  const blank = () => ({
    category: 'rent', label: '', scope: 'location',
    location_id: locations[0]?.id || '', entity: 'ETC',
    allocation: 'even', allocation_config: {}, amount: '', breakpoint: '',
    period: 'monthly', kind: 'standing', effective_month: thisMonth, notes: '',
  });

  async function load() {
    setLoading(true);
    try {
      const r = await api('GET', '/api/admin/fixed-costs', null, headers);
      setRows(Array.isArray(r?.fixed_costs) ? r.fixed_costs : []);
    } catch { setRows([]); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (patch) => setDraft((d) => ({ ...d, ...patch }));
  function openNew() { setEditing('new'); setDraft(blank()); setMsg(''); }
  function openEdit(r) {
    const known = CATEGORIES.some((c) => c.key === r.category);
    setEditing(r.id);
    setDraft({
      category: known ? r.category : 'other',
      label: r.label || (known ? '' : r.category),
      scope: r.scope, location_id: r.location_id || (locations[0]?.id || ''),
      entity: r.entity || 'ETC', allocation: r.allocation || 'even',
      allocation_config: (r.allocation_config && typeof r.allocation_config === 'object') ? r.allocation_config : {},
      amount: String(r.amount ?? ''), breakpoint: String(r.breakpoint ?? ''), period: r.period || 'monthly',
      kind: r.kind || 'standing', effective_month: monthOf(r.effective_month), notes: r.notes || '',
    });
    setMsg('');
  }
  function close() { setEditing(null); setDraft(null); setMsg(''); }

  async function save() {
    if (!draft) return;
    if (draft.category === 'other' && !draft.label.trim()) { setMsg('Name this cost (label).'); return; }
    if (!(Number(draft.amount) >= 0)) { setMsg(draft.category === 'percentage_rent' ? 'Rate % must be a number.' : 'Amount must be a number.'); return; }
    if (draft.category === 'percentage_rent' && !(Number(draft.breakpoint) >= 0)) { setMsg('Breakpoint (annual sales $) must be a number.'); return; }
    if (!/^\d{4}-\d{2}$/.test(draft.effective_month)) { setMsg('Pick an effective month.'); return; }
    setMsg('Saving…');
    const payload = {
      category: draft.category === 'other' ? draft.label.trim() : draft.category,
      label: draft.label.trim(), scope: draft.scope, amount: Number(draft.amount),
      period: draft.period, kind: draft.kind, effective_month: draft.effective_month, notes: draft.notes.trim(),
    };
    if (draft.category === 'percentage_rent') payload.breakpoint = Number(draft.breakpoint);
    if (draft.scope === 'location') payload.location_id = Number(draft.location_id);
    if (draft.scope === 'entity') payload.entity = draft.entity;
    if (draft.scope === 'entity' || draft.scope === 'org') {
      payload.allocation = draft.allocation;
      if (draft.allocation === 'ratio') payload.allocation_config = draft.allocation_config;
    }
    try {
      if (editing === 'new') await api('POST', '/api/admin/fixed-costs', payload, headers);
      else await api('PUT', `/api/admin/fixed-costs/${editing}`, payload, headers);
      close(); load();
    } catch (e) { setMsg(String(e?.message || e).slice(0, 200)); }
  }
  async function remove(r) {
    if (!window.confirm(`Delete ${catLabel(r.category)}${r.label ? ' · ' + r.label : ''}?`)) return;
    try { await api('DELETE', `/api/admin/fixed-costs/${r.id}`, null, headers); load(); } catch { /* keep row */ }
  }

  const attribution = (r) =>
    r.scope === 'location' ? (r.location_name || `Location ${r.location_id}`)
      : r.scope === 'entity' ? `${r.entity} (LLC)`
        : `Org · ${r.allocation || 'even'}`;

  const setRatio = (key, val) => upd({ allocation_config: { ...draft.allocation_config, [key]: val === '' ? '' : Number(val) } });
  const ratioTotal = draft
    ? Object.values(draft.allocation_config || {}).reduce((s, v) => s + (Number(v) || 0), 0) : 0;

  const input = 'border border-gray-300 rounded px-2 py-1 text-sm';

  return (
    <div className="space-y-4">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="text-base font-semibold">Fixed costs</div>
            <div className="text-xs text-gray-500">
              Overhead for the weekly &amp; daily P&amp;L — rent, utilities, insurance, taxes, professional fees.
              Enter utilities each month; standing costs carry forward until you change them.
            </div>
          </div>
          {!editing && <button onClick={openNew} className="btn-primary">Add cost</button>}
        </div>
      </div>

      {editing && draft && (
        <div className="card" style={{ borderLeft: '4px solid #b48a47' }}>
          <div className="text-sm font-semibold mb-2">{editing === 'new' ? 'New cost' : 'Edit cost'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <label className="text-xs text-gray-600">Category
              <select className={input + ' w-full mt-0.5'} value={draft.category} onChange={(e) => upd({ category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-600">{draft.category === 'other' ? 'Name *' : 'Label (optional)'}
              <input className={input + ' w-full mt-0.5'} value={draft.label} placeholder={draft.category === 'other' ? 'e.g. Alarm monitoring' : 'e.g. Electric, CPA'} onChange={(e) => upd({ label: e.target.value })} />
            </label>
            <label className="text-xs text-gray-600">Attribution
              <select className={input + ' w-full mt-0.5'} value={draft.scope} onChange={(e) => upd({ scope: e.target.value })}>
                <option value="location">A café</option>
                <option value="entity">A legal entity (LLC)</option>
                <option value="org">Whole org</option>
              </select>
            </label>
            {draft.scope === 'location' && (
              <label className="text-xs text-gray-600">Café
                <select className={input + ' w-full mt-0.5'} value={draft.location_id} onChange={(e) => upd({ location_id: e.target.value })}>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </label>
            )}
            {draft.scope === 'entity' && (
              <label className="text-xs text-gray-600">Entity (LLC)
                <select className={input + ' w-full mt-0.5'} value={draft.entity} onChange={(e) => upd({ entity: e.target.value })}>
                  {ENTITIES.map((en) => <option key={en} value={en}>{en}</option>)}
                </select>
              </label>
            )}
            <label className="text-xs text-gray-600">{draft.category === 'percentage_rent' ? 'Rate %' : 'Amount ($)'}
              <input className={input + ' w-full mt-0.5'} type="number" min="0" step="0.01" value={draft.amount} onChange={(e) => upd({ amount: e.target.value })} />
            </label>
            {draft.category === 'percentage_rent' && (
              <label className="text-xs text-gray-600">Breakpoint (annual sales $)
                <input className={input + ' w-full mt-0.5'} type="number" min="0" step="1" value={draft.breakpoint} placeholder="e.g. 500000" onChange={(e) => upd({ breakpoint: e.target.value })} />
                <span className="block text-[10px] text-gray-400">pay the rate on sales above this per year</span>
              </label>
            )}
            <label className="text-xs text-gray-600">Billed
              <select className={input + ' w-full mt-0.5'} value={draft.period} onChange={(e) => upd({ period: e.target.value })}>
                {PERIODS.map(([p]) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-600">Effective month
              <input className={input + ' w-full mt-0.5'} type="month" value={draft.effective_month} onChange={(e) => upd({ effective_month: e.target.value })} />
            </label>
            <label className="text-xs text-gray-600">Entry type
              <select className={input + ' w-full mt-0.5'} value={draft.kind} onChange={(e) => upd({ kind: e.target.value })}>
                <option value="standing">Standing (carries forward)</option>
                <option value="metered">Metered (re-enter each month)</option>
              </select>
            </label>
          </div>

          {(draft.scope === 'entity' || draft.scope === 'org') && (
            <div className="mt-3">
              <label className="text-xs text-gray-600">Split across cafés
                <select className={input + ' ml-2'} value={draft.allocation} onChange={(e) => upd({ allocation: e.target.value })}>
                  <option value="even">Evenly</option>
                  <option value="by_sales">By sales</option>
                  <option value="ratio">Custom %</option>
                  <option value="none">Keep at this level (no café split)</option>
                </select>
              </label>
              {draft.allocation === 'ratio' && (
                <div className="mt-2 p-2 rounded" style={{ background: '#faf8f4' }}>
                  <div className="text-xs text-gray-600 mb-1">Percent to each café, plus a corporate slice (e.g. the delivery van's share of an umbrella policy — kept out of café margins).</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {locations.map((l) => (
                      <label key={l.id} className="text-xs text-gray-600">{l.name}
                        <input className={input + ' w-16 ml-1'} type="number" min="0" max="100" step="0.1"
                          value={draft.allocation_config?.[l.id] ?? ''} onChange={(e) => setRatio(l.id, e.target.value)} />%
                      </label>
                    ))}
                    <label className="text-xs text-gray-600" style={{ fontWeight: 600 }}>Corporate / van
                      <input className={input + ' w-16 ml-1'} type="number" min="0" max="100" step="0.1"
                        value={draft.allocation_config?.corporate ?? ''} onChange={(e) => setRatio('corporate', e.target.value)} />%
                    </label>
                  </div>
                  <div className="text-xs mt-1" style={{ color: Math.abs(ratioTotal - 100) < 0.05 ? '#2e7d32' : '#b91c1c' }}>
                    Total: {ratioTotal.toFixed(1)}% {Math.abs(ratioTotal - 100) < 0.05 ? '✓' : '(should sum to 100)'}
                  </div>
                </div>
              )}
            </div>
          )}

          <label className="text-xs text-gray-600 block mt-3">Notes
            <input className={input + ' w-full mt-0.5'} value={draft.notes} onChange={(e) => upd({ notes: e.target.value })} />
          </label>

          <div className="mt-3" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={save} className="btn-primary">Save</button>
            <button onClick={close} className="text-sm text-gray-600 underline">Cancel</button>
            {msg && <span className="text-xs" style={{ color: msg === 'Saving…' ? '#5a6470' : '#b91c1c' }}>{msg}</span>}
          </div>
        </div>
      )}

      <div className="card">
        {loading ? <div className="text-sm text-gray-500">Loading…</div>
          : rows.length === 0 ? <div className="text-sm text-gray-500">No fixed costs yet. Add rent, utilities, insurance to get started.</div>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', color: '#757575' }}>
                      <th style={{ padding: 6 }}>Category</th>
                      <th style={{ padding: 6 }}>Label</th>
                      <th style={{ padding: 6 }}>Attribution</th>
                      <th style={{ padding: 6, textAlign: 'right' }}>Amount</th>
                      <th style={{ padding: 6 }}>Type</th>
                      <th style={{ padding: 6 }}>Effective</th>
                      <th style={{ padding: 6 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} style={{ borderBottom: '1px solid #f2f2f2' }}>
                        <td style={{ padding: 6, textTransform: 'capitalize' }}>{catLabel(r.category)}</td>
                        <td style={{ padding: 6 }}>{r.label || <span className="text-gray-400">—</span>}</td>
                        <td style={{ padding: 6 }}>{attribution(r)}</td>
                        <td style={{ padding: 6, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {r.category === 'percentage_rent'
                            ? <span>{Number(r.amount)}% <span className="text-gray-400">over {money(r.breakpoint)}/yr</span></span>
                            : <span>{money(r.amount)} <span className="text-gray-400">{periodShort(r.period)}</span></span>}
                        </td>
                        <td style={{ padding: 6 }}>{r.kind === 'metered' ? 'Metered' : 'Standing'}</td>
                        <td style={{ padding: 6, whiteSpace: 'nowrap' }}>{monthOf(r.effective_month)}</td>
                        <td style={{ padding: 6, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEdit(r)} className="text-xs underline text-gray-600 mr-2">Edit</button>
                          <button onClick={() => remove(r)} className="text-xs underline" style={{ color: '#b91c1c' }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>
    </div>
  );
}
